import { Form, Modal } from "antd";
import i18n from "i18next";
import { type FC } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps } from "../../../../../components/FeatureButtons/types";
import { Checkbox, SelectDynamic } from "../../../../../components/Form/formComponents";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";
import { dataGroupSelectorConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { useMoveSiteModalLogic } from "./useMoveSiteModalLogic";

export const MoveSiteModal: FC<ButtonProps<SiteEntity>> = ({ isDisabled, items }) => {
    const [form] = Form.useForm();

    const currentDataGroup = getAssignedDataGroup();
    const isDisabledFinal = isDisabled || items?.length === 0;

    const [modalState, callbacks] = useMoveSiteModalLogic(form, {
        formatErrors: true,
        errorMessageKey: "moveData.modal.importError",
        isBatch: false,
    });

    const { isModalVisible, uploading, importResult, showAllErrors } = modalState;
    const { showModal, onCancel, onOk } = callbacks;

    const handleOk = async () => {
        const formValues = form.getFieldsValue();
        await onOk(async () => ({
            orgUnitIds: items?.map((item) => item.id).join(","),
            dataGroupId: Number(formValues.dataGroup),
            useTargetSGValues: formValues.targetSiteGroupValues ?? true,
        }));
    };

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabledFinal}>
                {i18n.t("button.move")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("moveData.modal.title")}
                onOk={handleOk}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    importResult?.importSuccessCount ? null : (
                        <PrimaryButton key="import" onClick={handleOk} disabled={uploading}>
                            {i18n.t("button.move")}
                        </PrimaryButton>
                    ),
                ]}
            >
                <p>{i18n.t("moveData.modal.message")}</p>
                <Form form={form}>
                    <Form.Item name="dataGroup">
                        <SelectDynamic
                            name="dataGroup"
                            label={i18n.t("moveData.dataGroup")}
                            fieldConfig={dataGroupSelectorConfig}
                            filterOptions={(option) => option.value !== String(currentDataGroup.id)}
                        />
                    </Form.Item>
                    {uploading && <ProgressBar />}
                    <Form.Item name="targetSiteGroupValues" valuePropName="checked">
                        <Checkbox
                            label="orgUnitMove.targetSiteGroupValues"
                            readOnly={false}
                            defaultChecked={true}
                            name="targetSiteGroupValues"
                        />
                    </Form.Item>
                    {importResult && (
                        <RenderImportResult
                            importResult={importResult}
                            showAllErrors={showAllErrors}
                            setShowAllErrors={modalState.setShowAllErrors}
                            errorString="moveData.errorString"
                            successString="moveData.successString"
                        />
                    )}
                </Form>
            </Modal>
        </>
    );
};
